import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "./InsightsPage.css";

const articles = [
  {
    slug: "ai-crypto-signals-app-android",
    title: "AI Crypto Signals App For Smart Traders On Android",
    category: "Crypto & Stocks",
    readTime: "15-20 min read",
    highlight:
      "A deep dive into how an AI crypto signals app like Trend Genie works on Android, how to read AI signals, and how to combine them with solid risk management.",
    path: "/insights/ai-crypto-signals-app-android",
  },
  {
    slug: "risk-management-small-trading-accounts",
    title: "Risk Management For Small Trading Accounts Using AI Signals",
    category: "Risk Management",
    readTime: "15-20 min read",
    highlight:
      "Practical risk management strategies for small accounts, including position sizing, drawdown control, and how to safely use AI trading signals.",
    path: "/insights/risk-management-small-trading-accounts",
  },
  {
    slug: "crypto-signals-for-beginners",
    title: "Crypto Signals For Beginners: How To Read And Use Them Safely",
    category: "Beginner Guides",
    readTime: "15-20 min read",
    highlight:
      "A complete beginner-friendly guide to understanding crypto signals, common terms, and how to avoid the most frequent mistakes new traders make.",
    path: "/insights/crypto-signals-for-beginners",
  },
  {
    slug: "ai-trading-signals-android",
    title: "AI Trading Signals On Android: Turn Your Phone Into A Trading Assistant",
    category: "Mobile Trading",
    readTime: "15-20 min read",
    highlight:
      "How AI trading signals on Android fit into a busy schedule, and how to build a mobile-first workflow with Trend Genie.",
    path: "/insights/ai-trading-signals-android",
  },
  {
    slug: "ai-stock-analysis-app-android",
    title: "AI Stock Analysis App For Android: Smarter Market Research On The Go",
    category: "Stock Analysis",
    readTime: "10-12 min read",
    highlight:
      "How to use an AI stock analysis app on Android for faster research, cleaner watchlists, and better trade selection with Trend Genie.",
    path: "/insights/ai-stock-analysis-app-android",
  },
  {
    slug: "crypto-swing-trading-signals-strategy",
    title: "Crypto Swing Trading Signals: How To Build A Smarter Strategy",
    category: "Swing Trading",
    readTime: "10-12 min read",
    highlight:
      "A practical strategy for using crypto swing trading signals to align with trend, improve entries, and manage risk over multi-day moves.",
    path: "/insights/crypto-swing-trading-signals-strategy",
  },
  {
    slug: "trading-journal-ai-signals",
    title: "How To Build A Trading Journal For AI Signals",
    category: "Trading Psychology",
    readTime: "9-11 min read",
    highlight:
      "A clear process for tracking AI-generated trades so you can measure signal quality, find execution mistakes, and improve over time.",
    path: "/insights/trading-journal-ai-signals",
  },
  {
    slug: "best-indicators-confirm-ai-crypto-signals",
    title: "Best Indicators To Confirm AI Crypto Signals",
    category: "Technical Analysis",
    readTime: "9-11 min read",
    highlight:
      "A simple confirmation framework using moving averages, RSI, volume, and support-resistance to improve AI crypto signal selection.",
    path: "/insights/best-indicators-confirm-ai-crypto-signals",
  },
  {
    slug: "best-crypto-trading-app-android",
    title: "Best Crypto Trading App For Android In 2025: Features That Actually Matter",
    category: "Mobile Trading",
    readTime: "10-12 min read",
    highlight:
      "Not all crypto apps are built for active traders. Discover the 7 features that define the best crypto trading app for Android and how Trend Genie delivers all of them in one place.",
    path: "/insights/best-crypto-trading-app-android",
  },
  {
    slug: "fibonacci-trading-strategy-crypto",
    title: "Fibonacci Trading Strategy For Crypto: How To Use Retracement Levels Like A Pro",
    category: "Technical Analysis",
    readTime: "11-13 min read",
    highlight:
      "Master the 23.6%, 38.2%, 61.8%, and 78.6% Fibonacci levels for crypto. Learn which zones matter most, how support and resistance work, and how Trend Genie shows them live.",
    path: "/insights/fibonacci-trading-strategy-crypto",
  },
  {
    slug: "day-trading-crypto-ai-signals",
    title: "Day Trading Crypto With AI Signals: A Practical Android Guide",
    category: "Day Trading",
    readTime: "12-14 min read",
    highlight:
      "A complete framework for day trading crypto with AI signals on Android. Covers intraday entry logic, derivatives data interpretation, Fibonacci zones, risk rules, and daily routines.",
    path: "/insights/day-trading-crypto-ai-signals",
  },
];

function InsightsPage() {
  const baseUrl = "https://www.trendgenie.io";

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: articles.map((article, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `${baseUrl}${article.path}`,
      name: article.title,
    })),
  };

  return (
    <div className="page insights-page">
      <Helmet>
        <title>Genie Insights: AI Trading Articles and Guides | Trend Genie</title>
        <meta
          name="description"
          content="Genie Insights is the learning hub for Trend Genie users. Read in-depth articles on AI crypto signals, stock analysis, swing trading, risk management, and mobile trading workflows."
        />
        <meta
          name="keywords"
          content="AI crypto signals, AI trading signals, stock analysis app Android, crypto swing trading signals, trading journal AI signals, Trend Genie insights"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.trendgenie.io/insights" />
        <script type="application/ld+json">
          {JSON.stringify(itemListSchema)}
        </script>
      </Helmet>

      <section className="insights-hero">
        <h1>Genie Insights</h1>
        <p className="insights-subtitle">
          Long-form articles and practical guides on AI crypto signals, stock analysis, risk management, beginner
          education, and mobile-first trading with Trend Genie.
        </p>
      </section>

      <section className="insights-list">
        {articles.map((article) => (
          <article key={article.slug} className="insights-card">
            <div className="insights-card-meta">
              <span className="insights-category">{article.category}</span>
              <span className="insights-dot">|</span>
              <span className="insights-readtime">{article.readTime}</span>
            </div>

            <h2 className="insights-title">{article.title}</h2>
            <p className="insights-highlight">{article.highlight}</p>

            <Link to={article.path} className="insights-btn">
              Read article
            </Link>
          </article>
        ))}
      </section>
    </div>
  );
}

export default InsightsPage;
